import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatBRL, formatBRLouTraco, formatDateTimeBR } from "../lib/api";
import { fetchWithV2Fallback } from "../lib/v2";
import { canCancelOperation, formatTaxaPrazo } from "../lib/operations";
import { Plus, Search, Filter, XCircle, FileDown, FileText } from "lucide-react";
import StatusBadge from "../components/StatusBadge";
import CancelOperationDialog from "../components/CancelOperationDialog";
import { exportCSV, exportPDF } from "../lib/export";
import DateRangeFilter, { isInRange } from "../components/DateRangeFilter";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import useCanCancelOperations from "../hooks/use-can-cancel-operations";

// Vocabulário local + traduções do lifecycle da V2 (expirada/erros vêm do
// proxy normalizado — v2_proxy._V2_LIFECYCLE_TO_LOCAL).
const STATUS_FLOW = ["pendente", "aprovada", "contrato_enviado", "assinada", "aguardando", "liquidando", "liquidada", "recusada", "cancelada", "expirada", "erro_contrato", "erro_pagamento"];

const EXPORT_COLUMNS = [
  { key: "numero", label: "Número" },
  { key: "cedente_nome", label: "Cedente" },
  { key: "cedente_cnpj", label: "CNPJ" },
  { key: "product_nome", label: "Produto" },
  // "valor de face" — nomenclatura do domínio (diretriz do owner), fonte V2
  { key: "valor", label: "Valor de Face", format: (v) => formatBRL(v) },
  { key: "valor_liquido", label: "Líquido", format: (v) => formatBRLouTraco(v) },
  { key: "taxa_mensal", label: "Taxa (% a.m.)" },
  { key: "prazo_dias", label: "Prazo (dias)" },
  { key: "status", label: "Status" },
  { key: "created_at", label: "Criada em", format: (v) => formatDateTimeBR(v) },
];

export default function Operations() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "operator";
  const canCancel = user?.role === "admin";
  const canCancelOps = useCanCancelOperations(canCancel);
  const [ops, setOps] = useState([]);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [productFilter, setProductFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [cancelTarget, setCancelTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [opsResult, prodRes] = await Promise.all([
        fetchWithV2Fallback("/v2/operations", "/operations"),
        api.get("/products"),
      ]);
      const opsList = opsResult.data?.items || opsResult.data?.data || opsResult.data?.operations || (Array.isArray(opsResult.data) ? opsResult.data : []);
      setOps(opsList);
      // /products agora devolve { products, bo_unavailable } (catálogo híbrido);
      // aqui só precisamos da lista para o filtro por produto.
      setProducts(Array.isArray(prodRes.data) ? prodRes.data : (prodRes.data?.products || []));
    } catch (e) {
      // Falha de INFRA ao carregar. Logar e avisar de forma genérica (não engolir).
      console.error("[Operations] Falha ao carregar operacoes/produtos:", e);
      toast.error("Nao foi possivel carregar as operacoes agora. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return ops.filter((o) => {
      if (statusFilter !== "all" && o.status !== statusFilter) return false;
      if (productFilter !== "all" && o.product_id !== productFilter) return false;
      if ((dateFrom || dateTo) && !isInRange(o.created_at, dateFrom, dateTo)) return false;
      if (search) {
        const s = search.toLowerCase();
        if (
          !(o.numero || "").toLowerCase().includes(s) &&
          !(o.cedente_nome || "").toLowerCase().includes(s) &&
          !(o.cedente_cnpj || "").includes(s)
        )
          return false;
      }
      return true;
    });
  }, [ops, search, statusFilter, productFilter, dateFrom, dateTo]);

  return (
    <div className="space-y-6" data-testid="operations-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <span className="zemo-overline">Operações</span>
          <h1 className="mt-1.5 text-3xl sm:text-[34px] font-semibold tracking-tight text-zinc-900">
            Antecipações &amp; Crédito
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            Gerencie operações de antecipação de recebíveis e liberação de crédito.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="h-10"
            disabled={filtered.length === 0}
            onClick={() => exportCSV("operacoes-zemo", EXPORT_COLUMNS, filtered)}
            data-testid="export-csv-btn"
          >
            <FileDown className="h-4 w-4 mr-1.5" /> CSV
          </Button>
          <Button
            variant="outline"
            className="h-10"
            disabled={filtered.length === 0}
            onClick={() => exportPDF("Relatório de Operações", "operacoes-zemo", EXPORT_COLUMNS, filtered)}
            data-testid="export-pdf-btn"
          >
            <FileText className="h-4 w-4 mr-1.5" /> PDF
          </Button>
          {canEdit && (
            <Link to="/operacoes/nova" data-testid="new-operation-link">
              <Button className="bg-[#0055FF] hover:bg-[#0044CC] text-white h-10 font-semibold">
                <Plus className="h-4 w-4 mr-1.5" /> Nova Operação
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="zemo-card p-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex-1 relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            placeholder="Buscar por número, cedente ou CNPJ…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10"
            data-testid="operations-search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-zinc-400" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px] h-10" data-testid="filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos status</SelectItem>
              {STATUS_FLOW.map((s) => (
                <SelectItem key={s} value={s} className="capitalize">
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={productFilter} onValueChange={setProductFilter}>
            <SelectTrigger className="w-[180px] h-10" data-testid="filter-product">
              <SelectValue placeholder="Produto" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos produtos</SelectItem>
              {products.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:ml-auto">
          <DateRangeFilter
            from={dateFrom}
            to={dateTo}
            onChange={({ from, to }) => {
              if (from !== undefined) setDateFrom(from);
              if (to !== undefined) setDateTo(to);
            }}
            testidPrefix="ops"
          />
        </div>
      </div>

      {/* Table */}
      <div className="zemo-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50/60 text-[11px] uppercase tracking-wider text-zinc-500 border-b border-[var(--zemo-border)]">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Número</th>
                <th className="text-left px-5 py-3 font-semibold">Cedente</th>
                <th className="text-left px-5 py-3 font-semibold">Produto</th>
                <th className="text-right px-5 py-3 font-semibold">Valor de Face</th>
                <th className="text-right px-5 py-3 font-semibold">Líquido</th>
                <th className="text-right px-5 py-3 font-semibold">Taxa/Prazo</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="text-left px-5 py-3 font-semibold">Criada em</th>
                <th className="text-right px-5 py-3 font-semibold"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--zemo-border)]" data-testid="operations-table-body">
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-5 py-12 text-center text-zinc-500">
                    Nenhuma operação encontrada com os filtros atuais.
                  </td>
                </tr>
              )}
              {filtered.map((op) => (
                <tr key={op.id} className="hover:bg-zinc-50/60 transition-colors" data-testid={`operation-row-${op.numero}`}>
                  <td className="px-5 py-3.5 font-mono-num font-semibold text-zinc-900">{op.numero}</td>
                  <td className="px-5 py-3.5">
                    <div className="font-medium text-zinc-900">{op.cedente_nome}</div>
                    <div className="text-[11px] text-zinc-500 font-mono-num">{op.cedente_cnpj}</div>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="text-zinc-800">{op.product_nome}</div>
                    <div className="text-[11px] text-zinc-500 capitalize">{op.product_tipo}</div>
                  </td>
                  <td className="px-5 py-3.5 text-right font-semibold text-zinc-900 font-mono-num">
                    {formatBRL(op.valor)}
                  </td>
                  <td className="px-5 py-3.5 text-right text-zinc-700 font-mono-num">
                    {formatBRLouTraco(op.valor_liquido)}
                  </td>
                  <td className="px-5 py-3.5 text-right text-[12px] text-zinc-600 font-mono-num">
                    {formatTaxaPrazo(op)}
                  </td>
                  <td className="px-5 py-3.5">
                    <StatusBadge status={op.status} />
                  </td>
                  <td className="px-5 py-3.5 text-[12px] text-zinc-500">{formatDateTimeBR(op.created_at)}</td>
                  <td className="px-5 py-3.5 text-right">
                    {/* Gate repassado da V2: só sincronizada + WAITING_APPROVAL.
                        Órfã local nunca oferece cancelar (item 23b-iv). */}
                    {canCancel && canCancelOps && canCancelOperation(op) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setCancelTarget(op)}
                        data-testid={`cancel-op-${op.numero}`}
                      >
                        <XCircle className="h-3.5 w-3.5 mr-1" /> Cancelar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 border-t border-[var(--zemo-border)] text-[12px] text-zinc-500 flex items-center justify-between">
          <span>
            Exibindo <span className="font-semibold text-zinc-900">{filtered.length}</span> de {ops.length} operações
          </span>
        </div>
      </div>

      {/* Diálogo de cancelamento compartilhado (Operações + Dashboard) */}
      <CancelOperationDialog
        operation={cancelTarget}
        onClose={() => setCancelTarget(null)}
        onCancelled={load}
      />
    </div>
  );
}
